// Deep links into the legacy .NET participant portal (webaccountlink). V3 is
// READ-ONLY — every "take me to the real thing" lever hands off to the legacy page
// that owns the write flow. This file is the ONE map of those destinations: add a row
// here when the .NET team supplies another .aspx path; never put a portal URL literal
// in a component.
//
// Base URL is env-driven (CRA bakes REACT_APP_* at build time — restart `npm start`
// after changing it) so UAT and prod point at their own hosts.

/** Legacy portal origin, no trailing slash. */
export const LEGACY_PORTAL_BASE_URL = (
  process.env.REACT_APP_LEGACY_PORTAL_URL || 'https://dev-www.webaccountlink.com'
).replace(/\/+$/, '');

// The participant's legacy dashboard on this environment's own portal host, without
// RANDOMNUM. buildLegacyUrl() returns it only if `participantDashboard` itself ever loses
// its path; an unmapped key otherwise lands on that route, RANDOMNUM included.
export const PORTAL_FALLBACK_URL = `${LEGACY_PORTAL_BASE_URL}/pcs1/ParticipantDashboard.aspx?LINK=35`;

/**
 * Route key → destination on LEGACY_PORTAL_BASE_URL. `null` means "path not supplied
 * yet" — buildLegacyUrl() falls back to the participant dashboard, so filling one in later is
 * a one-line change here and NO change at the call site.
 *
 * A destination is a bare path string, `{ path, params }` when the legacy page is
 * addressed by a query param as well — several .aspx pages serve different views off
 * one URL (`psplanacct.aspx?LINK=7`) — or `{ path, method: 'POST', fields }` for a page
 * the portal reaches by form POST. Those params/fields belong HERE, with the path they
 * are part of, and not at the call site: a bare `LINK: 7` in a component is the same
 * magic literal this map exists to keep out of components. RANDOMNUM is never listed —
 * the builder attaches it to every legacy link.
 */
export const LEGACY_ROUTES = {
  // The Relius pages that frame this dashboard. Loading one
  // fresh from the server is how a new handoff token is issued, so the "session expired"
  // screen's retry goes here rather than reloading the frame (FM-3035).
  participantDashboard: { path: '/pcs1/ParticipantDashboard.aspx', params: { LINK: 35 } },
  sponsorDashboard: { path: '/pcs1/SponsorDashboard.aspx', params: { USERTYPE: 'S', LINK: 7 } },

  // CONFIRMED. Contribution elections — the Dashboard contributions widget's
  // "Change rate" link and the Manage page's contribution-rate lever.
  contributions: '/pcs1/contribinfo.aspx',

  // CONFIRMED. The transaction ledger — ActivityCard's "View transactions" door,
  // the ActionStrip's transaction-history action and BalanceHero's pending strip.
  history: '/pcs1/transhist.aspx',

  // CONFIRMED. Plan balance by money source — the sponsor hero's "View balance by
  // source" drill-out. `psplanacct.aspx` is the plan-account page and `LINK` picks the
  // view within it, so the param is part of the destination, not a caller's choice.
  balanceBySource: { path: '/pcs1/psplanacct.aspx', params: { LINK: 7 } },

  // CONFIRMED. Web/Phone request history — a DIFFERENT ledger from `history` (the
  // requests a participant raised, not the money that moved), so it gets its own key:
  // ActivityCard's "View Web/Phone activity" door.
  webPhoneActivity: '/pcs1/voiceupdate.aspx',

  // CONFIRMED. AccountLink "Manage Account" — the ManagedAccountCard's
  // "View my managed account" door on the Dashboard.
  managedAccount: '/pcs1/manageinvestments.aspx',

  // CONFIRMED. AccountLink → iJoin SAML hand-off — the ReadinessCard's "Review your
  // strategy" CTA. The portal POSTs this page with two fixed fields (seen in DevTools,
  // 2026-09-09); a GET stalls on the page, so the route carries the form.
  reviewStrategy: {
    path: '/pcs1/OutBoundSAML.aspx',
    method: 'POST',
    fields: { SAMLSP: 'ijoin', IJDESTINATION: '/retirementSnapshot' },
  },

  // CONFIRMED. "Manage account" — the ActionStrip's one write lever. The legacy page it
  // lands on is investment MANAGEMENT (manageinvestments.aspx), which is where every
  // transactional flow starts. Same page as `managedAccount` above, reached from a
  // different door, so it keeps its own key: the two can be pointed apart later without
  // touching either call site.
  // LINK=36 is the left-nav entry the legacy page highlights on arrival; it travels with
  // the path for the same reason balanceBySource's does.
  manage: { path: '/pcs1/manageinvestments.aspx', params: { LINK: 36 } },

  // CONFIRMED. Personal rate of return — the ActionStrip's ROR lookup.
  ror: { path: '/pcs1/eereturn.aspx', params: { LINK: 40 } },

  // CONFIRMED (supplied 2026-09-16). Sponsor · forfeitures — the plan-balance card's
  // "Forfeitures" source row. The legacy page calls the account a SUSPENSE account, hence
  // the path. `LINK` is carried EMPTY because that is the link the portal itself hands
  // out (…/pssuspense.aspx?RANDOMNUM=&LINK=): this page highlights no left-nav entry, and
  // an empty param is a URL the legacy app is known to serve while an absent one is not —
  // the same reasoning `buildLegacyUrl` applies to RANDOMNUM.
  forfeitures: { path: '/pcs1/pssuspense.aspx', params: { LINK: '' } },

  // CONFIRMED (supplied 2026-09-16). Sponsor · the plan's loan summary. One key for both
  // spellings of the row — a plan that calls it "Loans outstanding" and one that calls it
  // "Loan" both publish `detail: "loans"` — and for the loans modal's own drill-out.
  loans: { path: '/pcs1/psloansum.aspx', params: { LINK: 7 } },

  // CONFIRMED (supplied 2026-09-17). Sponsor · the six quick-action tiles on the sponsor
  // dashboard. These are the legacy portal's SPONSOR pages (the `ps*` family), a
  // different set from the participant ones above, so each takes its own key rather
  // than reusing `contributions` / `history` / `webPhoneActivity` — those point at the
  // PARTICIPANT elections page and ledgers, and pointing one pair apart later must not
  // move the other. No LINK was supplied with these paths, so none is sent and the
  // legacy page opens on its own default view.
  sponsorContributions: '/pcs1/psdvc.aspx',          // Submit contributions (payroll/DVC)
  sponsorParticipants: '/pcs1/psssnsearch.aspx',     // View participants (SSN search)
  sponsorTransactions: '/pcs1/pstranshist.aspx',     // Transactions (plan ledger)
  sponsorReporting: '/pcs1/psreports.aspx',          // Reporting (compliance & census)
  sponsorWebRequests: '/pcs1/psvoiceupdate.aspx',    // Web requests (pending & history)
  sponsorInvestmentInfo: '/pcs1/psinvestmentprofiles.aspx', // Investment info (lineup & models)

  // CONFIRMED (supplied 2026-09-28). Sponsor · "Submit a case" on the customer care card.
  // The legacy sponsor page serves several forms off one URL and `KEY` picks the
  // case-submission one, so the param is part of the destination, not a caller's choice
  // (same reasoning as balanceBySource's LINK).
  sponsorSubmitCase: { path: '/pcs1/pcssponsorpage.aspx', params: { KEY: 'PSP' } },

  // No dedicated path supplied by the .NET team yet. Interim: the legacy Manage Account
  // page (same destination as `manage`), where these flows start in this environment —
  // not the portal login, which signed an embedded participant out of context.
  investments: { path: '/pcs1/manageinvestments.aspx', params: { LINK: 36 } }, // Manage · investment directions
  rebalance: { path: '/pcs1/manageinvestments.aspx', params: { LINK: 36 } },   // Manage · rebalance
  transfers: { path: '/pcs1/manageinvestments.aspx', params: { LINK: 36 } },   // Manage · fund-to-fund transfer
};

/**
 * Sponsor quick-action id (as the plan payload publishes it) -> LEGACY_ROUTES key.
 *
 * The hand-off goes through this map instead of using the id as a route key directly,
 * because the sponsor payload's vocabulary overlaps the participant routes: the
 * payload's `contributions` is the sponsor's payroll submission, while the
 * `contributions` route is the PARTICIPANT's elections page. An id that isn't listed
 * here has no legacy page, and the caller falls back to acknowledging the click.
 */
export const SPONSOR_ACTION_ROUTES = {
  contributions: 'sponsorContributions',
  participants: 'sponsorParticipants',
  transactions: 'sponsorTransactions',
  reporting: 'sponsorReporting',
  webRequests: 'sponsorWebRequests',
  investmentInfo: 'sponsorInvestmentInfo',
};

const LEGACY_PORTAL = {
  LEGACY_PORTAL_BASE_URL, PORTAL_FALLBACK_URL, LEGACY_ROUTES, SPONSOR_ACTION_ROUTES,
};
export default LEGACY_PORTAL;
